import { Component, computed, effect, input, output, signal, viewChild, viewChildren } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ICampaignCreatorForm } from '@campaign-form/campaign-form.interface';
import { NumberSuffixPipe } from '@core/pipes/number-suffix.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { CtaSummaryPanelComponent } from '@pages/campaigns/campaign-form/form-fields/creators-and-ctas/cta-summary-panel/cta-summary-panel.component';
import { EDeliverableFlowStep } from '@pages/campaigns/campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-flow-dialog.interface';
import { TCampaignApi } from '@pages/campaigns/campaigns.interface';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { ICreatorCtaTagCounts, TCreatorSummaryCta } from './creator-summary-panel.interface';

@Component({
  imports: [
    CtaSummaryPanelComponent,
    IconBadgeComponent,
    IconComponent,
    MatButtonModule,
    MatExpansionModule,
    MatMenuModule,
    MatTooltipModule,
    NumberSuffixPipe,
    TranslateModule,
  ],
  selector: 'app-creator-summary-panel',
  templateUrl: './creator-summary-panel.component.html',
})
export class CreatorSummaryPanelComponent {
  public readonly campaignCreator = input.required<FormGroup<ICampaignCreatorForm>['value']>();
  public readonly campaign = input.required<TCampaignApi | null>();
  public readonly ctasShareCountMap = input.required<Map<string, number>>();
  public readonly creatorHasCustomValueMap = input.required<Map<string, boolean>>();
  public readonly getExistingDeliverablesCount = input.required<(ctaId: string, creatorId: string) => number>();
  public readonly getExistingExecutionsCount = input.required<(ctaId: string, creatorId: string) => number | null>();
  public readonly getExistingKpisCount = input.required<(ctaId: string, creatorId: string) => number>();
  public readonly hasCreatorBriefingNote = input<boolean>(false);

  public readonly addPayoutClick = output<void>();
  public readonly deleteCreatorClick = output<void>();
  public readonly briefingNoteClick = output<void>();
  public readonly addIndividualGroupClick = output<void>();
  public readonly addDeliverablesForCta = output<{ ctaId: string; step: EDeliverableFlowStep }>();
  public readonly editCtaName = output<string>();
  public readonly editCtaGroup = output<string>();
  public readonly removeCtaFromCreator = output<{ ctaId: string; isAllCreators: boolean }>();
  public readonly copyCtaForCreator = output<string>();
  public readonly copyCta = output<string>();

  public readonly isNew = input<boolean>(false);

  public readonly expansionPanel = viewChild.required(MatExpansionPanel);
  public readonly isExpanded = signal<boolean>(false);

  constructor() {
    effect(() => {
      if (this.isNew()) {
        this.expansionPanel().open();
      }
    });
  }
  public readonly allCtasExpanded = computed(() => {
    const panels = this.ctaPanels();

    return panels.length > 0 && panels.every((p) => p.isExpanded());
  });

  public readonly EDeliverableFlowStep = EDeliverableFlowStep;

  public readonly tagCounts = computed((): ICreatorCtaTagCounts => {
    const creatorId = this.campaignCreator().creatorId ?? '';
    const ctas = (this.campaignCreator().ctas as TCreatorSummaryCta[]) ?? [];
    const counts: ICreatorCtaTagCounts = {
      global: 0,
      globalCustomized: 0,
      individual: 0,
      shared: 0,
      sharedCustomized: 0,
    };

    ctas.forEach((cta) => {
      const shareCount = this.ctasShareCountMap().get(cta.id!) ?? 0;
      const customized = this.creatorHasCustomValueMap().get(`${creatorId}_${cta.id!}`) ?? false;

      if (cta.isAllCreators) {
        if (customized) {
          counts.globalCustomized++;
        } else {
          counts.global++;
        }
      } else if (shareCount > 1) {
        if (customized) {
          counts.sharedCustomized++;
        } else {
          counts.shared++;
        }
      } else {
        counts.individual++;
      }
    });

    return counts;
  });

  public readonly hasPayout = computed(() => {
    const c = this.campaignCreator();
    const campaign = this.campaign();

    return campaign?.budget && campaign.currency && c.cost !== null && c.cost !== undefined && c.cost !== '';
  });

  public readonly ctaInfoMap = computed(() => {
    const creatorId = this.campaignCreator().creatorId ?? '';
    const map = new Map<string, { shareCount: number; hasCustomValue: boolean }>();

    (this.campaignCreator().ctas as TCreatorSummaryCta[])?.forEach((cta) => {
      map.set(cta.id!, {
        hasCustomValue: this.creatorHasCustomValueMap().get(`${creatorId}_${cta.id!}`) ?? false,
        shareCount: this.ctasShareCountMap().get(cta.id!) ?? 0,
      });
    });

    return map;
  });

  private readonly ctaPanels = viewChildren(CtaSummaryPanelComponent);

  public expandCollapseAllCtas(): void {
    const shouldExpand = !this.allCtasExpanded();

    this.ctaPanels().forEach((p) => (shouldExpand ? p.expansionPanel().open() : p.expansionPanel().close()));
  }
}
